import { useState, useEffect } from 'react'
import { apiGet, apiPost, apiPut, apiDelete } from '../../services/api'
import { UserPlus, Search, Edit2, X, Eye, EyeOff, UserX, UserCheck, RefreshCw, Cloud, Database } from 'lucide-react'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import { usePermissions } from '../../hooks/usePermissions'

interface User {
  id: number
  name: string
  email: string
  role: string
  phone?: string
  employee_id?: string
  is_active: boolean
  department?: string
  designation?: string
  source?: 'ev' | 'local'
}

interface EVEmployee {
  id: number
  name: string
  email: string
  phone?: string
  employee_id?: string
  role?: { name?: string; slug?: string } | string
  roles?: Array<{ name?: string; display_name?: string }>
  ev_role?: string
  employee_detail?: {
    department?: { name?: string; team_name?: string }
    designation?: { name?: string }
    employee_id?: string
  }
  department?: { name?: string; team_name?: string }
  designation?: { name?: string }
  status?: string
  is_active?: boolean
}

interface UserFormData {
  name: string
  email: string
  password: string
  role: string
  phone: string
  employee_id: string
}

const initialFormData: UserFormData = {
  name: '',
  email: '',
  password: '',
  role: 'supervisor',
  phone: '',
  employee_id: '',
}

export default function UsersPage() {
  const { can } = usePermissions()
  const [users, setUsers] = useState<User[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [formData, setFormData] = useState<UserFormData>(initialFormData)
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [roleFilter, setRoleFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [dataSource, setDataSource] = useState<'ev' | 'local'>('local')
  const [isRefreshing, setIsRefreshing] = useState(false)

  const mapEVEmployee = (emp: EVEmployee): User => {
    const detail = emp.employee_detail || {}
    const dept = detail.department || emp.department
    const desig = detail.designation || emp.designation

    // Department uses team_name in EV API
    const deptName = dept?.team_name || dept?.name || ''

    // Role: try role object, role string, roles[] array
    let roleName = ''
    if (emp.role && typeof emp.role === 'object') {
      roleName = emp.role.slug || emp.role.name || ''
    } else if (typeof emp.role === 'string') {
      roleName = emp.role
    }
    if (!roleName && emp.roles && emp.roles.length > 0) {
      roleName = emp.roles[0].name || ''
    }
    // Fallback: use ev_role enriched by backend from local DB
    if (!roleName && emp.ev_role) {
      roleName = emp.ev_role
    }

    return {
      id: emp.id,
      name: emp.name || '',
      email: emp.email || '',
      phone: emp.phone || '',
      employee_id: detail.employee_id || emp.employee_id || '',
      role: roleName,
      is_active: emp.status === 'active' || emp.is_active !== false,
      department: deptName,
      designation: desig?.name || '',
      source: 'ev',
    }
  }

  const fetchFromEV = async (): Promise<boolean> => {
    try {
      const params: Record<string, string | number> = { per_page: 100 }
      if (search) params.search = search

      const response = await apiGet<{ data: EVEmployee[] }>('/eagle-vision/hr/employees', params)
      const mapped = (response.data || []).map(mapEVEmployee)

      // Apply client-side filters
      let filtered = mapped
      if (roleFilter) {
        filtered = filtered.filter(u => u.role.toLowerCase().includes(roleFilter.toLowerCase()))
      }
      if (statusFilter === 'true') {
        filtered = filtered.filter(u => u.is_active)
      } else if (statusFilter === 'false') {
        filtered = filtered.filter(u => !u.is_active)
      }

      setUsers(filtered)
      setDataSource('ev')
      return true
    } catch {
      return false
    }
  }

  const fetchFromLocal = async () => {
    try {
      const params: Record<string, string> = {}
      if (search) params.search = search
      if (roleFilter) params.role = roleFilter
      if (statusFilter) params.is_active = statusFilter

      const response = await apiGet<{ items: User[] }>('/users', params)
      setUsers((response.items || []).map(u => ({ ...u, source: 'local' as const })))
      setDataSource('local')
    } catch (err) {
      console.error('Failed to load users:', err)
    }
  }

  const fetchUsers = async () => {
    // Try EV first, fall back to local
    const evSuccess = await fetchFromEV()
    if (!evSuccess) {
      await fetchFromLocal()
    }
    setIsLoading(false)
    setIsRefreshing(false)
  }

  useEffect(() => {
    fetchUsers()
  }, [search, roleFilter, statusFilter])

  const handleRefresh = () => {
    setIsRefreshing(true)
    fetchUsers()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsSubmitting(true)

    try {
      if (editingUser) {
        const updateData: Record<string, string> = {
          name: formData.name,
          phone: formData.phone,
          employee_id: formData.employee_id,
        }
        if (formData.password) {
          updateData.password = formData.password
        }
        await apiPut(`/users/${editingUser.id}`, updateData)
      } else {
        await apiPost('/users', {
          name: formData.name,
          email: formData.email,
          password: formData.password,
          role: formData.role,
          phone: formData.phone,
          employee_id: formData.employee_id,
        })
      }

      setIsModalOpen(false)
      setEditingUser(null)
      setFormData(initialFormData)
      fetchUsers()
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save user')
    } finally {
      setIsSubmitting(false)
    }
  }

  const openCreateModal = () => {
    setEditingUser(null)
    setFormData(initialFormData)
    setError('')
    setShowPassword(false)
    setIsModalOpen(true)
  }

  const openEditModal = (user: User) => {
    setEditingUser(user)
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      phone: user.phone || '',
      employee_id: user.employee_id || '',
    })
    setError('')
    setShowPassword(false)
    setIsModalOpen(true)
  }

  const toggleUserStatus = async (user: User) => {
    try {
      if (user.is_active) {
        await apiDelete(`/users/${user.id}`)
      } else {
        await apiPut(`/users/${user.id}`, { is_active: true })
      }
      fetchUsers()
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to update user status')
    }
  }

  const getRoleBadgeClass = (role: string) => {
    const r = role.toLowerCase()
    if (r === 'company_admin' || r === 'admin') return 'bg-purple-100 text-purple-800'
    if (r === 'supervisor') return 'bg-blue-100 text-blue-800'
    if (r === 'employee') return 'bg-green-100 text-green-800'
    return 'bg-gray-100 text-gray-800'
  }

  const getRoleLabel = (role: string) => {
    const r = role.toLowerCase()
    if (r === 'company_admin') return 'Admin'
    if (r === 'admin') return 'Admin'
    if (r === 'supervisor') return 'Supervisor'
    if (r === 'employee') return 'Employee'
    return role
  }

  if (isLoading) {
    return <div className="flex items-center justify-center h-64"><LoadingSpinner size="lg" /></div>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users</h1>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-gray-500">Manage company users and employees</p>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
              dataSource === 'ev' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
            }`}>
              {dataSource === 'ev' ? <Cloud className="h-3 w-3" /> : <Database className="h-3 w-3" />}
              {dataSource === 'ev' ? 'Eagle Vision' : 'Local'}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="p-2 text-gray-400 hover:text-primary-600 hover:bg-gray-100 rounded-lg"
            title="Refresh"
          >
            <RefreshCw className={`h-5 w-5 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
          {can('admin_users', 'create') && (
            <button onClick={openCreateModal} className="btn-primary">
              <UserPlus className="h-5 w-5 mr-2" /> Add User
            </button>
          )}
        </div>
      </div>

      <div className="card">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email, or employee ID..."
              className="input pl-10"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className="input w-full sm:w-40"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option value="">All Roles</option>
            <option value="admin">Admin</option>
            <option value="supervisor">Supervisor</option>
            <option value="employee">Employee</option>
          </select>
          <select
            className="input w-full sm:w-40"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </div>

        {/* Users Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Name</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Email</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Employee ID</th>
                {dataSource === 'ev' && (
                  <>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Department</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Designation</th>
                  </>
                )}
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Role</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">Status</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan={dataSource === 'ev' ? 8 : 6} className="py-8 text-center text-gray-500">
                    No users found.
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div className="font-medium text-gray-900">{user.name}</div>
                      {user.phone && <div className="text-sm text-gray-500">{user.phone}</div>}
                    </td>
                    <td className="py-3 px-4 text-gray-600">{user.email}</td>
                    <td className="py-3 px-4 text-gray-600">{user.employee_id || '-'}</td>
                    {dataSource === 'ev' && (
                      <>
                        <td className="py-3 px-4 text-gray-600">{user.department || '-'}</td>
                        <td className="py-3 px-4 text-gray-600">{user.designation || '-'}</td>
                      </>
                    )}
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeClass(user.role)}`}>
                        {getRoleLabel(user.role)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={user.is_active ? 'badge-success' : 'badge-danger'}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-center gap-1">
                        {can('admin_users', 'edit') && user.source === 'local' && (
                          <button
                            onClick={() => openEditModal(user)}
                            className="p-2 text-gray-400 hover:text-primary-600 hover:bg-gray-100 rounded"
                            title="Edit user"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                        )}
                        {can('admin_users', 'edit') && user.source === 'local' && (
                          <button
                            onClick={() => toggleUserStatus(user)}
                            className={`p-2 rounded ${
                              user.is_active
                                ? 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                                : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
                            }`}
                            title={user.is_active ? 'Deactivate user' : 'Activate user'}
                          >
                            {user.is_active ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                          </button>
                        )}
                        {user.source === 'ev' && (
                          <span className="text-xs text-gray-400">EV managed</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-xl font-bold">
                {editingUser ? 'Edit User' : 'Add New User'}
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name *
                </label>
                <input
                  type="text"
                  className="input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Doe"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  className="input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@company.com"
                  required
                  disabled={!!editingUser}
                />
                {editingUser && (
                  <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {editingUser ? 'New Password (leave empty to keep current)' : 'Password *'}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="input pr-10"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder={editingUser ? 'Enter new password' : 'Min. 8 characters'}
                    minLength={editingUser ? 0 : 8}
                    required={!editingUser}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role *
                </label>
                <select
                  className="input"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  disabled={!!editingUser}
                >
                  <option value="supervisor">Supervisor</option>
                  <option value="company_admin">Company Admin</option>
                </select>
                {editingUser && (
                  <p className="text-xs text-gray-500 mt-1">Role cannot be changed</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Employee ID
                  </label>
                  <input
                    type="text"
                    className="input"
                    value={formData.employee_id}
                    onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                    placeholder="EMP001"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="text"
                    className="input"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+62 812 3456 7890"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="btn-secondary"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <LoadingSpinner size="sm" />
                      <span className="ml-2">Saving...</span>
                    </>
                  ) : (
                    editingUser ? 'Update User' : 'Create User'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
